import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { warehouseApi } from 'api/warehouse';
import { Button } from 'shadcn/components/ui/Button';
import { Input } from 'shadcn/components/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shadcn/components/ui/Select';

function BulkMovePalletActionBar({
    destinationLocation,
    facilityID,
    isLoading,
    onDestinationLocationChange,
    onMove,
    onPalletsFiltered,
    onSearchChange,
    pallets,
    searchQuery,
    selectedCount,
}) {
    const [availableLocations, setAvailableLocations] = useState([]);
    const [loadingLocations, setLoadingLocations] = useState(false);

    // Load available locations for bulk move
    useEffect(() => {
        const fetchLocations = async () => {
            setLoadingLocations(true);
            const response = await warehouseApi.getBulkMoveEnabledLocations(facilityID);
            if (response.status && response.data?.rows?.length > 0) {
                const locationOptions = response.data.rows
                    .map((location) => ({
                        label: location.name,
                        value: location.id.toString(),
                        name: location.name,
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label));

                requestAnimationFrame(() => {
                    setAvailableLocations(locationOptions);
                });
            } else {
                setAvailableLocations([]);
            }
            setLoadingLocations(false);
        };

        if (facilityID) {
            fetchLocations();
        }
    }, [facilityID]);

    // Filter pallets based on search query
    const getFilteredPallets = () => {
        if (!searchQuery.trim()) {
            return pallets;
        }

        const query = searchQuery.toLowerCase().trim();
        return pallets.filter((pallet) => {
            if (!pallet || typeof pallet.palletId !== 'number') {
                return false;
            }
            return String(pallet.palletId).includes(query);
        });
    };

    useEffect(() => {
        onPalletsFiltered(getFilteredPallets());
    }, [searchQuery, pallets, onPalletsFiltered]);

    const handleLocationChange = (locationId) => {
        const selectedLocation = availableLocations.find((loc) => loc.value === locationId);
        onDestinationLocationChange(locationId, selectedLocation?.name || '');
    };

    return (
        <div className="px-6 py-4 border-b">
            <div className="flex items-center gap-4">
                <Input
                    placeholder="Search pallets..."
                    value={searchQuery}
                    onChange={(e) => onSearchChange(e.target.value)}
                    className="w-[200px]"
                />
                <span className="text-sm text-gray-600 whitespace-nowrap">
                    {selectedCount} of {getFilteredPallets().length} selected
                </span>
                <span className="text-sm text-gray-600 whitespace-nowrap">Bulk move to:</span>
                <Select value={destinationLocation} onValueChange={handleLocationChange} disabled={loadingLocations}>
                    <SelectTrigger className="w-48">
                        <SelectValue placeholder="Select location" />
                    </SelectTrigger>
                    <SelectContent
                        position="popper"
                        sideOffset={4}
                        className="max-h-[300px] overflow-auto"
                        align="start"
                    >
                        {availableLocations.length === 0 ? (
                            <div className="p-2 text-sm text-gray-500 text-center">
                                {loadingLocations ? 'Loading locations...' : 'No locations available'}
                            </div>
                        ) : (
                            availableLocations.map((location) => (
                                <SelectItem key={location.value} value={location.value}>
                                    {location.label}
                                </SelectItem>
                            ))
                        )}
                    </SelectContent>
                </Select>
                <Button
                    variant="default"
                    disabled={!destinationLocation || selectedCount === 0 || isLoading}
                    onClick={onMove}
                >
                    Confirm Move
                </Button>
            </div>
        </div>
    );
}

BulkMovePalletActionBar.propTypes = {
    destinationLocation: PropTypes.string.isRequired,
    facilityID: PropTypes.number.isRequired,
    isLoading: PropTypes.bool,
    onDestinationLocationChange: PropTypes.func.isRequired,
    onMove: PropTypes.func.isRequired,
    onPalletsFiltered: PropTypes.func.isRequired,
    onSearchChange: PropTypes.func.isRequired,
    pallets: PropTypes.arrayOf(
        PropTypes.shape({
            palletId: PropTypes.number.isRequired,
        }),
    ).isRequired,
    searchQuery: PropTypes.string.isRequired,
    selectedCount: PropTypes.number.isRequired,
};

BulkMovePalletActionBar.defaultProps = {
    isLoading: false,
};

export { BulkMovePalletActionBar };
